import { useRouteError } from "react-router-dom";
import { EukaBarkProvider, ErrorPage } from "@euka-mfes/bark";
import { logger } from "@/utils/loggingUtils";
import { getBaseRoute } from "@/utils/route";
import { useEffect } from "react";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@/utils/chunkLoadUtils";

/**
 * Route-level error boundary for lesson routes (PLAN-309).
 *
 * Catches any unhandled render errors within the lesson route tree and
 * displays a controlled error UI instead of React Router's default
 * "Unexpected Application Error" page.
 *
 * Used as the `errorElement` on the lesson route in learningRouter.tsx.
 */
const LessonRouteErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    const message =
      error instanceof Error ? error.message : String(error ?? "Unknown error");
    const stack = error instanceof Error ? error.stack : undefined;

    // Stale chunk after a deploy: reload once instead of reporting an error.
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk({ source: "LessonRouteErrorBoundary" });
      logger.warn("Stale chunk caught by LessonRouteErrorBoundary", {
        error: message
      });
      return;
    }

    logger.error("Lesson route render error caught by LessonRouteErrorBoundary", {
      error: message,
      stack
    });
  }, [error]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleDashboard = () => {
    window.location.href = getBaseRoute();
  };

  return (
    <EukaBarkProvider>
      <ErrorPage
        statusCode={500}
        bgPosition="full"
        statusTitle="Something went wrong"
        statusDescription="We hit an unexpected error loading this lesson. Please try reloading the page or return to the dashboard."
        btnPrimaryText="Reload Page"
        onClickBtnPrimary={handleReload}
        btnSecondaryText="Return to dashboard"
        onClickBtnSecondary={handleDashboard}
      />
    </EukaBarkProvider>
  );
};

export default LessonRouteErrorBoundary;
